"use client";

import { RequestType, SellCarRequestCreateDTO } from "@alba-cars/common-modules";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { toast } from "sonner";

import CarDetailsStep from "./CarDetailsStep";
import PersonalInformationStep from "./PersonalInformationStep";
import SecondStep from "./SecondStep";
import MultistepForm, { StepData } from "@/components/common/forms/MultiStepForm";
import { useRouter } from "@/i18n/routing";
import { getOrCreateLead } from "@/lib/api/lead/get-or-create-lead";
import { createSellCarRequest } from "@/lib/api/sell-car-request/create-sell-car-request";
import { notifyWebhookAboutSellCarUpdate } from "@/lib/api/sell-car-request/notify-update-webhook";
import { readUTMCookies } from "@/lib/utm-tracker";
import {
  createFirstStepSchema,
  createSecondStepSchema,
  createSellCarFormSchema,
  SellCarFormSchemaType,
  ThirdStepSchema,
} from "@/schemas/validation/sellCarFormSchema";

interface ButtonLabels {
  back: string;
  next: string;
  submit: string;
  uploading: string;
}

interface ClientFormWrapperProps {
  title: string;
  description: string;
  buttonLabels: ButtonLabels;
}

export default function ClientFormWrapper({ title, description, buttonLabels }: ClientFormWrapperProps) {
  const t = useTranslations("SellCar");
  const tValidation = useTranslations("SellCar.form.validation");
  const router = useRouter();

  const schemas = useMemo(
    () => ({
      0: createFirstStepSchema(tValidation),
      1: createSecondStepSchema(tValidation),
      2: ThirdStepSchema,
    }),
    [tValidation],
  );

  const sellCarFormSchema = useMemo(() => createSellCarFormSchema(tValidation), [tValidation]);

  const stepsData: StepData[] = [
    {
      component: PersonalInformationStep,
      onStepSubmit: async (_formData: FormData, _previousFormData: FormData) => {},
      step: { label: t("steps.personalDetails"), index: 0 },
    },
    {
      component: CarDetailsStep,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        const parsedData = schemas[0].parse(previousFormData);
        const marketingData = readUTMCookies();

        const response = await getOrCreateLead({
          ...parsedData,
          source: "sell-car",
          status: "ABANDONED_FORM",
          webhookData: {
            sellCarDetails: { ...formData },
            vehicleImageUrls: [],
            source: "sell-car-form",
            ...(marketingData ? { marketingDetails: marketingData } : {}),
          },
        });

        if (!response.isNew) {
          await notifyWebhookAboutSellCarUpdate({
            lead: { ...parsedData, id: response.data!.id },
            sellCarDetails: { ...formData },
            vehicleImageUrls: [],
            message: "Client updated with car details",
            source: "sell-car-form",
            ...(marketingData ? { marketingDetails: marketingData } : {}),
          });
        }
      },
      step: { label: t("steps.carDetails"), index: 1 },
    },
    {
      component: SecondStep,
      onStepSubmit: async (formData: FormData, previousFormData: FormData) => {
        const { name, phone, ...rest } = previousFormData as any;
        const marketingData = readUTMCookies();
        const data = formData as any;
        const imageUrls = (data["images"] ?? []) as unknown as any[];

        const updatePayload = {
          lead: { name, phone },
          sellCarDetails: { ...rest },
          vehicleImageUrls: imageUrls?.map((image) => image.url),
          message: "Client updated with car images",
          source: "sell-car-form",
          ...(marketingData ? { marketingDetails: marketingData } : {}),
        };

        await notifyWebhookAboutSellCarUpdate(updatePayload);
      },
      step: { label: t("steps.images"), index: 2 },
      submitStep: true,
    },
  ];

  const stepTitles = [t("steps.step1"), t("steps.step2"), t("steps.step3")];

  const handleFinalSubmit = async (data: SellCarFormSchemaType) => {
    try {
      const marketingData = readUTMCookies();

      const parsedData = sellCarFormSchema.parse({
        ...data,
        marketing: marketingData,
      });

      let dto = new SellCarRequestCreateDTO() as Omit<SellCarRequestCreateDTO, "validate" | "toPlain" | "emirates">;
      dto = {
        ...parsedData,
        requestType: RequestType.SELL,
        description: parsedData.description ?? "",
        ...(marketingData ? { marketing: marketingData } : {}),
      };

      if (parsedData.modelVersion) {
        dto.modelVersion = parsedData.modelVersion;
        dto.modelVersionName = parsedData.modelVersionName ?? "";
      }

      const response = await createSellCarRequest(dto);

      const token = `sc_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem("sellCarToken", token);
      sessionStorage.setItem(
        "sellCarData",
        JSON.stringify({
          ...dto,
          brandName: response.brandName,
          modelName: response.modelName,
          year: response.year,
          mileage: response.mileage,
        }),
      );
      router.push("/thank-you/sell-used-car-dubai?ref=success");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(t("toast.error"));
    }
  };

  return (
    <MultistepForm<SellCarFormSchemaType>
      stepsData={stepsData}
      onSubmit={handleFinalSubmit}
      stepTitles={stepTitles}
      title={title}
      description={description}
      schemas={schemas}
      showSubmitOnLastStep={true}
      buttonLabels={buttonLabels}
    />
  );
}
